import { AccountInfo, AuthenticationResult, AuthError, BrowserAuthError, BrowserAuthErrorCodes, Configuration, EventMessage, EventType, PublicClientApplication, RedirectRequest, SilentRequest, InteractionRequiredAuthError } from '@azure/msal-browser';
import { appConfig } from '../AppConfig';
import { appInsights } from '../AppInsights';
import i18n from '../i18n';

// standard code to use MSAL with EntraID
const msalConfiguration: Configuration = {
    auth: {
        clientId: appConfig.CLIENT_ID,
        authority: `https://login.microsoftonline.com/${appConfig.TENANT_ID}/v2.0`, // important to put v2.0 to request EntraID v2 token
        redirectUri: appConfig.REDIRECT_URI,
    },
};

// requested scopes are defined in the appConfig
// additional Microsoft Graph API scopes needs to implicitely be defined as optional claims in the EntraID app registration
export const loginRequest: RedirectRequest = {
    //scopes: ['openid', 'profile', 'offline_access', 'email', 'User.Read'].concat(appConfig.MIDDLEWARE_API_SCOPES),
    scopes: appConfig.MIDDLEWARE_API_SCOPES
};

// requested scopes are defined in the appConfig
// additional Microsoft Graph API scopes needs to implicitely be defined as optional claims in the EntraID app registration
export const userRequest: SilentRequest = {
    //scopes: ['openid', 'profile', 'offline_access', 'email', 'User.Read'],
    scopes: appConfig.MIDDLEWARE_API_SCOPES
};

// MSAL raises these while it is still doing its job: a redirect already running in this or another
// tab, or its hidden token-renewal iframe. They resolve on their own, so they must never be tracked
// as exceptions nor send the user to the error page.
const transientAuthErrorCodes: string[] = [
    BrowserAuthErrorCodes.interactionInProgress,
    BrowserAuthErrorCodes.blockIframeReload,
    BrowserAuthErrorCodes.hashEmptyError,
    BrowserAuthErrorCodes.redirectInIframe,
    BrowserAuthErrorCodes.userCancelled,
    BrowserAuthErrorCodes.noTokenRequestCacheError,
];

export function isTransientAuthError(error: unknown): boolean {
    return error instanceof AuthError && transientAuthErrorCodes.includes(error.errorCode);
}

export const publicClientApp = new PublicClientApplication(msalConfiguration);
function setActiveAccount(account: AccountInfo) {
    publicClientApp.setActiveAccount(account);
}

// MSAL v3 requires initialize() to be awaited before rendering.
// Export the promise so index.tsx can await it before calling root.render().
export const msalInitialized = publicClientApp.initialize().then(() => {
    const accounts = publicClientApp.getAllAccounts();
    if (accounts.length > 0) {
        setActiveAccount(accounts[0]);
    }
});

publicClientApp.addEventCallback((event: EventMessage) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
        const payload = event.payload as AuthenticationResult;
        const account = payload.account;
        if (account) {
            setActiveAccount(account);
        }
    } else if (event.eventType === EventType.LOGIN_FAILURE) {
        reportLoginFailure(event.error);
    }
});

// Both this module and the MsalAuthenticationTemplate error component can observe the same failure.
let loginFailureReported = false;

export function reportLoginFailure(error: unknown) {
    if (isTransientAuthError(error)) {
        console.log('Transient authentication error, letting MSAL settle:', (error as AuthError).errorCode);
        return;
    }
    if (loginFailureReported) {
        return;
    }
    loginFailureReported = true;
    const loginError = new Error(`Login failed: ${(error as Error | undefined)?.message || 'Unknown error'}`);
    appInsights.trackException({ exception: loginError });
    console.error('Login failed:', error);
    redirectToErrorPage('login_failure', i18n.t('error.api.auth.loginFailure'));
}

// Tracks a redirect already triggered by acquireAccessToken so concurrent callers don't each start their own.
let redirectInProgress = false;

function fallbackToInteractiveRedirect(scopes: string[], account: AccountInfo) {
    if (redirectInProgress) {
        return;
    }
    redirectInProgress = true;
    console.log('Silent token acquisition failed, redirecting to interactive login');
    publicClientApp.acquireTokenRedirect({ scopes, account }).catch(() => {
        redirectInProgress = false;
    });
}

export const acquireAccessToken = async (scopes: string[]): Promise<string | undefined> => {
    const account = publicClientApp.getActiveAccount();
    if (!account) {
        const error = new Error('No active account found');
        appInsights.trackException({ exception: error });
        console.error('No active account found');
        redirectToErrorPage('no_account', i18n.t('error.api.auth.noAccount'));
        return undefined;
    }

    try {
        const accessToken = await publicClientApp.acquireTokenSilent({
            scopes,
            account,
        });
        return accessToken.accessToken;
    } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
            // Handle specific MSAL errors - these error codes are standard MSAL error codes
            switch (error.errorCode) {
                case 'interaction_required':
                    // Fallback to interactive login - this will redirect the user
                    fallbackToInteractiveRedirect(scopes, account);
                    // No return value as redirect will handle the flow
                    return undefined;
                case 'consent_required':
                    const consentError = new Error(`Consent required for scopes: ${scopes.join(', ')}`);
                    appInsights.trackException({ exception: consentError });
                    console.error('Consent required for scopes:', scopes);
                    redirectToErrorPage('consent_required', i18n.t('error.api.auth.consentRequired'));
                    break;
                case 'claims_challenge':
                    const claimsError = new Error(`Claims challenge required: ${error.message}`);
                    appInsights.trackException({ exception: claimsError });
                    console.error('Claims challenge required:', error);
                    redirectToErrorPage('claims_challenge', i18n.t('error.api.auth.claimsChallenge'));
                    break;
                default:
                    const tokenError = new Error(`Token acquisition error: ${error.errorCode} - ${error.message}`);
                    appInsights.trackException({ exception: tokenError });
                    console.error('Token acquisition error:', error);
                    redirectToErrorPage('token_acquisition_failure', i18n.t('error.api.auth.tokenAcquisitionFailure'));
            }
        } else if (isTransientAuthError(error)) {
            // Expected MSAL states (redirect already under way, response handled in the renewal iframe): nothing to report.
            console.log('Skipping transient authentication error:', (error as AuthError).errorCode);
        } else if (error instanceof BrowserAuthError && error.errorCode === BrowserAuthErrorCodes.monitorWindowTimeout) {
            // Silent iframe renewal didn't respond in time (e.g. blocked 3rd-party cookies) - fall back to an interactive redirect.
            fallbackToInteractiveRedirect(scopes, account);
        } else {
            const unexpectedError = new Error(`Unexpected error during token acquisition: ${(error as Error).message}`);
            appInsights.trackException({ exception: unexpectedError });
            console.error('Unexpected error during token acquisition:', error);
            redirectToErrorPage('unexpected_error', i18n.t('error.api.auth.unexpectedError'));
        }
        return undefined;
    }
};

export function getCurrentAccount(): AccountInfo | null {
    const account = publicClientApp.getActiveAccount();
    if (!account) {
        const warning = new Error('No current account found');
        appInsights.trackException({ exception: warning });
        console.warn('No current account found');
    }
    return account;
}

export async function logout() {
    try {
        await publicClientApp.logoutRedirect();
    } catch (error) {
        const logoutError = new Error(`Logout failed: ${(error as Error).message}`);
        appInsights.trackException({ exception: logoutError });
        console.error('Logout failed:', error);
        redirectToErrorPage('logout_failure', i18n.t('error.api.auth.logoutFailure'));
    }
}

// Helper function to redirect to error page with error details
function redirectToErrorPage(errorCode: string, errorMessage: string) {
    const errorParams = new URLSearchParams({
        errorCode,
        errorMessage,
    });
    window.location.href = `/error?${errorParams.toString()}`;
}

